import { api } from './api';
import type {
  Prompt,
  PaginatedPromptResponse,
  PromptVersion,
  PromptChangeLogEntry,
} from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Request types aligned to BE prompt_schema.py (module_master removed project-wide).
// Required: type, name, aiRole, systemRole, objective.
export interface PromptCreateRequest {
  type: string;
  name: string;
  aiRole: string;
  systemRole: string;
  objective: string;
  iconPath?: string | null;
  tech?: string | null;
  taskInstructions?: string | null;
  taskInput?: string | null;
  taskOutputFormat?: string | null;
  taskExample?: Record<string, unknown> | null;
  llm?: string | null;
  settingsJson?: Record<string, unknown> | null;
  modelId?: string | null;   // AI model ObjectId (nullable)
  evalIds?: string[];        // Eval ObjectIds
  appId?: string;   // Owning project (honoured by the BE only for callers who can access it)
}

// Update payload mirrors create, plus versioning: content edits become a version.
export type PromptUpdateRequest = PromptCreateRequest & {
  /** Why — recorded in the change log. */
  changeNote?: string | null;
  /** ACTIVE publishes now; DRAFT keeps it as the draft and leaves the live prompt alone. */
  saveAs?: 'ACTIVE' | 'DRAFT';
};

// BE /prompts/list query params (snake_case, matches FastAPI Query names).
export interface PromptListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string; // JSON per-column filters
  app_id?: string;  // Filter to one accessible project (intersected server-side)
  include_global?: boolean; // Also include global/core rows (show-global toggle)
}

/** Re-scope preview: which projects are aligned to a prompt. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, Record<string, number>>;
  references: Record<string, Record<string, number>>;
}

/** One thing that references a prompt — enough to name it and link to it. */
export interface PromptDependentItem {
  id: string | null;
  name: string;
  appId: string | null;
}

/** A kind of dependent (agents, RAG versions, …) with its count and a sample. */
export interface PromptDependentGroup {
  type: 'agent' | 'rag' | 'eval_suite' | 'conversation' | 'document_intelligence';
  label: string;
  count: number;
  items: PromptDependentItem[];
}

/** The downstream dependency graph of a prompt — what embeds or targets it. */
export interface PromptDependents {
  promptId: string;
  total: number;
  groups: PromptDependentGroup[];
}

export const promptsService = {
  // ── Versions & change log ──────────────────────────────────────────────
  listVersions: async (promptId: string): Promise<PromptVersion[]> =>
    (await api.get<ApiResponse<PromptVersion[]>>(`/api/v1/prompts/${promptId}/versions`)).data.data,
  getVersion: async (promptId: string, version: number): Promise<PromptVersion> =>
    (await api.get<ApiResponse<PromptVersion>>(`/api/v1/prompts/${promptId}/versions/${version}`)).data.data,
  activateVersion: async (promptId: string, version: number, changeNote?: string): Promise<Prompt> =>
    (await api.post<ApiResponse<Prompt>>(`/api/v1/prompts/${promptId}/versions/${version}/activate`,
      { changeNote: changeNote || null })).data.data,
  discardDraft: async (promptId: string, changeNote?: string): Promise<void> => {
    await api.delete(`/api/v1/prompts/${promptId}/draft`, { data: { changeNote: changeNote || null } });
  },
  /** Every tag the version should carry; a tag already on another version moves here. */
  setVersionTags: async (promptId: string, version: number, tags: string[], changeNote?: string): Promise<string[]> =>
    (await api.put<ApiResponse<{ tags: string[] }>>(`/api/v1/prompts/${promptId}/versions/${version}/tags`,
      { tags, changeNote: changeNote || null })).data.data.tags,
  getChangeLog: async (promptId: string): Promise<PromptChangeLogEntry[]> =>
    (await api.get<ApiResponse<PromptChangeLogEntry[]>>(`/api/v1/prompts/${promptId}/change-log`)).data.data,

  // List all prompts with pagination + optional search (BE filters by name/type/aiRole/llm)
  list: async (params?: PromptListParams): Promise<PaginatedPromptResponse> => {
    const response = await api.get<PaginatedPromptResponse>('/api/v1/prompts/list', { params });
    return response.data;
  },

  // Get prompt by ID
  getById: async (promptId: string): Promise<Prompt> => {
    const response = await api.get<ApiResponse<Prompt>>(`/api/v1/prompts/get/${promptId}`);
    return response.data.data;
  },

  // Downstream dependents: agents, RAG versions, eval suites, conversations and
  // the Document-Intelligence capability that embed or target this prompt.
  getDependents: async (promptId: string): Promise<PromptDependents> => {
    const response = await api.get<ApiResponse<PromptDependents>>(`/api/v1/prompts/${promptId}/dependents`);
    return response.data.data;
  },

  // Preview re-scoping a prompt to a project (platform admin only).
  getRescopePreview: async (id: string, appId?: string | null): Promise<RescopePreview> => {
    const response = await api.get<{ data: RescopePreview }>(
      `/api/v1/prompts/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change a prompt's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<Prompt> => {
    const response = await api.patch<{ data: Prompt }>(
      `/api/v1/prompts/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  // Create new prompt
  create: async (data: PromptCreateRequest): Promise<Prompt> => {
    const response = await api.post<ApiResponse<Prompt>>('/api/v1/prompts/create', data);
    return response.data.data;
  },

  // Update prompt (BE uses PUT)
  update: async (promptId: string, data: PromptUpdateRequest): Promise<Prompt> => {
    const response = await api.put<ApiResponse<Prompt>>(`/api/v1/prompts/update/${promptId}`, data);
    return response.data.data;
  },

  // Soft delete prompt
  softDelete: async (promptId: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/prompts/delete/${promptId}`);
    return response.data.data;
  },

  // Restore a soft-deleted prompt
  restore: async (promptId: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/prompts/restore/${promptId}`);
    return response.data.data;
  },
};
